/**
 * Felder von „Verlauf eingrenzen“: Zeiträume (Aufenthalt und Berechnet überall, Eingereicht bzw.
 * Bestätigt je Ansicht), Auslöser und Ergebnis als Mehrfachauswahl, Zustellung oder Art je Ansicht
 * und die technischen Filter mit Hinweis, solange eine ID unvollständig ist.
 */
import type { DynamicPricingHistoryView } from '@/shared/api';
import { HistoryChoiceChips } from './HistoryChoiceChips';
import { pendingId, type HistoryFilterDraft, type HistorySpanKey } from './historyFilterDraft';
import { EXPOSURE_KIND, OUTCOME, PUBLICATION_FILTER_LABEL, TRIGGER } from './historyText';
import { DATE_MAX, dayInput, utcInput, utcInstant } from './historyTimeInput';
import { EXPOSURE_KINDS, OUTCOMES, PUBLICATION_STATUSES, TRIGGERS } from './historyUrlState';

const OUTCOME_LABEL = Object.fromEntries(Object.entries(OUTCOME).map(([k, v]) => [k, v.label]));
const SPANS: Record<DynamicPricingHistoryView, [HistorySpanKey, string][]> = {
  publications: [
    ['publicationCreatedFrom', 'Eingereicht ab'],
    ['publicationCreatedTo', 'Eingereicht bis'],
  ],
  decisions: [],
  exposures: [
    ['exposureConfirmedFrom', 'Bestätigt ab'],
    ['exposureConfirmedTo', 'Bestätigt bis'],
  ],
};
const field =
  'border-line bg-card rounded-control flex flex-col gap-0.5 border px-3 py-2 text-[11px] text-ink-soft';
const input = 'text-ink min-h-7 bg-transparent text-[13px] font-bold outline-none';

export function HistoryFilterFields({
  view,
  draft,
  onChange,
}: {
  view: DynamicPricingHistoryView;
  draft: HistoryFilterDraft;
  onChange: (next: HistoryFilterDraft) => void;
}) {
  const set = (patch: Partial<HistoryFilterDraft>) => onChange({ ...draft, ...patch });
  const spans: [HistorySpanKey, string][] = [
    ['stayFrom', 'Aufenthalt ab'],
    ['stayTo', 'Aufenthalt bis'],
    ['decisionFrom', 'Berechnet ab'],
    ['decisionTo', 'Berechnet bis'],
    ...SPANS[view],
  ];
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-2 min-[360px]:grid-cols-2">
        {spans.map(([key, label]) => {
          const day = key.startsWith('stay');
          return (
            <label key={key} className={field}>
              {label}
              <input
                type={day ? 'date' : 'datetime-local'}
                max={DATE_MAX[day ? 'date' : 'datetime-local']}
                className={input}
                value={day ? (draft[key] ?? '') : utcInput(draft[key])}
                onChange={(event) =>
                  set({
                    [key]: day ? dayInput(event.target.value) : utcInstant(event.target.value),
                  })
                }
              />
            </label>
          );
        })}
      </div>
      <HistoryChoiceChips
        title="Auslöser"
        options={TRIGGERS}
        labels={TRIGGER}
        value={draft.trigger}
        onChange={(trigger) => set({ trigger })}
      />
      <HistoryChoiceChips
        title="Ergebnis"
        options={OUTCOMES}
        labels={OUTCOME_LABEL}
        value={draft.outcome}
        onChange={(outcome) => set({ outcome })}
      />
      {view === 'publications' && (
        <HistoryChoiceChips
          title="Zustellung"
          options={PUBLICATION_STATUSES}
          labels={PUBLICATION_FILTER_LABEL}
          value={draft.publicationStatus}
          onChange={(publicationStatus) => set({ publicationStatus })}
        />
      )}
      {view === 'exposures' && (
        <HistoryChoiceChips
          title="Art"
          options={EXPOSURE_KINDS}
          labels={EXPOSURE_KIND}
          value={draft.exposureKind}
          onChange={(exposureKind) => set({ exposureKind })}
        />
      )}
      <details
        className="border-hairline rounded-control border px-3"
        open={pendingId(draft.configurationText) || pendingId(draft.profileText) || undefined}
      >
        <summary className="flex min-h-11 cursor-pointer items-center text-[13px] font-bold">
          Technische Filter
        </summary>
        <div className="flex flex-col gap-2 pb-3">
          {(
            [
              ['configurationText', 'Einstellungs-Version (ID)'],
              ['profileText', 'Strategie-Version (ID)'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className={field}>
              {label}
              <input
                className={`${input} font-mono`}
                value={draft[key]}
                spellCheck={false}
                onChange={(event) => set({ [key]: event.target.value })}
              />
              {pendingId(draft[key]) && (
                <span className="text-amber-ink">Noch unvollständig – wirkt noch nicht.</span>
              )}
            </label>
          ))}
          <span className="text-ink-soft text-[11px]">
            Wirkt erst, wenn die ID vollständig ist; eine halbe Eingabe bleibt nach „Anzeigen“
            stehen.
          </span>
        </div>
      </details>
    </div>
  );
}
